import { FC, useMemo } from "react";
import { formatMoney, getQueryDateParam, timeFormatter } from "../../../../common/utils/helpers";
import { Column } from "react-table";
import { Table } from "../../../../common/components/Table/Table";
import { SelectColumnFilter } from "../../../../common/components/Table/SelectColumnFilter";
import { TextColumnFilter } from "../../../../common/components/Table/TextColumnFilter";
import { UserPopup } from "../../popups/UserPopup";
import { useGetOrdersVisorQuery } from "services/admin/adminOrdersApi";
import { QueryLayout } from "common/components/Layout/QueryLayout";
import { SkeletonTable } from "common/components/Table/SkeletonTable";
import { IOrder } from "common/models/order/IOrder";
import { NotFoundTitle } from "common/components/Title/NotFoundTitle";
import { ORDER_STATUS } from "common/utils/consts";
import { IOrdersReportTableProps } from "./CompletedOrderTable";

export const OrderVisorTable: FC<
  IOrdersReportTableProps & { query?: string }
> = ({ dateFrom, dateTo, vehicleCategory, vehicleType, query }) => {
  const {
    data: orders,
    isLoading,
    isFetching,
    error,
  } = useGetOrdersVisorQuery({
    date_from: getQueryDateParam(dateFrom),
    date_to: getQueryDateParam(dateTo),
    vehicle_category_id: vehicleCategory,
    vehicle_type_id: vehicleType,
    query,
  });

  const paymentMethodOptions = useMemo(
    () => distinctOptions(orders, (order) => order.payment_method?.name),
    [orders]
  );
  const statusOptions = useMemo(
    () => distinctOptions(orders, (order) => order.status?.name),
    [orders]
  );
  const vehicleCategoryOptions = useMemo(
    () => distinctOptions(orders, (order) => order.vehicle_category?.name),
    [orders]
  );

  const columns = useMemo(
    () => getColumns(paymentMethodOptions, statusOptions, vehicleCategoryOptions),
    [paymentMethodOptions, statusOptions, vehicleCategoryOptions]
  );

  return (
    <QueryLayout
      isLoading={isLoading}
      error={error}
      skeleton={<SkeletonTable rowCount={6} colCount={columns.length} />}
    >
      {orders?.length ? (
        <Table
          data={orders}
          columns={columns}
          isLoading={isFetching}
          sortable
          getRowClassName={(order) => (isOrderPaid(order) ? "bg-lightGreen" : "")}
        />
      ) : (
        <NotFoundTitle />
      )}
    </QueryLayout>
  );
};

const fullName = (
  person?: Nullable<{
    first_name?: string;
    middle_name?: string;
    last_name?: string;
  }>
) =>
  [person?.last_name, person?.first_name, person?.middle_name]
    .filter(Boolean)
    .join(" ") || "—";

// Отдельного поля "оплачено клиентом" бэкенд не отдаёт: считаем заказ
// оплаченным, если он прошёл стадию ожидания/ошибки оплаты
const UNPAID_ORDER_STATUSES = new Set<number>([
  ORDER_STATUS.PROCESSING,
  ORDER_STATUS.IMPLEMENTING_COMPANY_SELECTION,
  ORDER_STATUS.PAYMENT_WAITING,
  ORDER_STATUS.PAYMENT_ERROR,
]);

const PAYMENT_STATUS = {
  PAID: "Оплачено",
  UNPAID: "Не оплачено",
  REFUNDED: "Возврат",
};

const isOrderCancelled = (order: IOrder) => order.status?.id === ORDER_STATUS.REJECTED;

// Заказ считаем полностью оплаченным, только если он прошёл стадию оплаты и
// не был отменён — при отмене средства возвращаются клиенту
const isOrderPaid = (order: IOrder) =>
  !isOrderCancelled(order) && !UNPAID_ORDER_STATUSES.has(order.status?.id);

const getPaymentStatus = (order: IOrder) => {
  if (isOrderCancelled(order)) return PAYMENT_STATUS.REFUNDED;
  return isOrderPaid(order) ? PAYMENT_STATUS.PAID : PAYMENT_STATUS.UNPAID;
};

const PAYMENT_STATUS_COLOR: Record<string, string> = {
  [PAYMENT_STATUS.PAID]: "text-green",
  [PAYMENT_STATUS.UNPAID]: "text-red",
  [PAYMENT_STATUS.REFUNDED]: "text-gray",
};

const isExecutorPaid = (order: IOrder) => Boolean(order.payout_approved_at);

function distinctOptions<T>(
  items: Undefinable<T[]>,
  getValue: (item: T) => Undefinable<string>
) {
  const seen = new Set<string>();
  const options: { value: string; label: string }[] = [];
  items?.forEach((item) => {
    const value = getValue(item);
    if (value && !seen.has(value)) {
      seen.add(value);
      options.push({ value, label: value });
    }
  });
  return options;
}

// Значение "0" — выбор пункта "-" в SelectColumnFilter (сброс фильтра)
const selectFilter: Column<IOrder>["filter"] = (rows, columnIds, filterValue) => {
  if (!filterValue || filterValue === "0") return rows;
  return rows.filter((row) => row.values[columnIds[0]] === filterValue);
};

const makeAmountFilter = (
  getValue: (order: IOrder) => Undefinable<Nullable<number>>
): Column<IOrder>["filter"] =>
  (rows, _columnIds, filterValue) => {
    const query = String(filterValue).replace(/\s/g, "").toLowerCase();
    return rows.filter((row) => {
      const value = getValue(row.original);
      return (
        String(value ?? "").includes(query) ||
        formatMoney(value).replace(/\s/g, "").toLowerCase().includes(query)
      );
    });
  };

const getColumns = (
  paymentMethodOptions: { value: string; label: string }[],
  statusOptions: { value: string; label: string }[],
  vehicleCategoryOptions: { value: string; label: string }[]
): Column<IOrder>[] => [
  {
    Header: "№ заявки",
    id: "id",
    accessor: (order) => order.id,
    Cell: ({ row }: any) => <span className="font-semibold">{row.original.id}</span>,
    Filter: (filter) => <TextColumnFilter {...filter} />,
  },
  {
    Header: "Дата заявки",
    id: "created_at",
    accessor: (order) => new Date(order.created_at).getTime(),
    Cell: ({ row }: any) => (
      <span className="whitespace-nowrap">
        {timeFormatter({
          time: new Date(row.original.created_at),
          mode: "dd-MM-yyyy hh:mm",
        })}
      </span>
    ),
    Filter: (filter) => <TextColumnFilter {...filter} />,
    filter: (rows, _columnIds, filterValue) => {
      const query = String(filterValue).toLowerCase();
      return rows.filter((row) =>
        timeFormatter({
          time: new Date(row.original.created_at),
          mode: "dd-MM-yyyy hh:mm",
        })
          .toLowerCase()
          .includes(query)
      );
    },
  },
  {
    Header: "Заказчик (ФИО)",
    id: "customer_name",
    accessor: (order) => fullName(order),
    Filter: (filter) => <TextColumnFilter {...filter} />,
  },
  {
    Header: "Сумма от заказчика, руб.",
    id: "price",
    accessor: (order) => order.price ?? 0,
    Cell: ({ row }: any) => <>{formatMoney(row.original.price)}</>,
    Filter: (filter) => <TextColumnFilter {...filter} />,
    filter: makeAmountFilter((order) => order.price),
  },
  {
    Header: "Форма оплаты",
    id: "payment_method",
    accessor: (order) => order.payment_method?.name ?? "—",
    Filter: (filter) => <SelectColumnFilter {...filter} options={paymentMethodOptions} />,
    filter: selectFilter,
  },
  {
    Header: "Исполнитель",
    id: "executor_company",
    accessor: (order) => order.executor_company?.name ?? "—",
    Cell: ({ row: { original } }: { row: { original: IOrder } }) => (
      <div
        className={`-mx-[10px] -my-[10px] flex items-center px-[10px] py-[10px] ${
          isExecutorPaid(original) ? "bg-green text-white" : ""
        }`}
      >
        <UserPopup performer={original.executor_company} />
      </div>
    ),
    Filter: (filter) => <TextColumnFilter {...filter} />,
  },
  {
    Header: "Статус заявки",
    id: "status",
    accessor: (order) => order.status?.name ?? "—",
    Filter: (filter) => <SelectColumnFilter {...filter} options={statusOptions} />,
    filter: selectFilter,
  },
  {
    Header: "Статус оплаты",
    id: "payment_status",
    accessor: (order) => getPaymentStatus(order),
    Cell: ({ row }: any) => {
      const status = getPaymentStatus(row.original);
      return (
        <span className={`font-semibold ${PAYMENT_STATUS_COLOR[status]}`}>{status}</span>
      );
    },
    Filter: (filter) => (
      <SelectColumnFilter
        {...filter}
        options={[
          { value: PAYMENT_STATUS.PAID, label: PAYMENT_STATUS.PAID },
          { value: PAYMENT_STATUS.UNPAID, label: PAYMENT_STATUS.UNPAID },
          { value: PAYMENT_STATUS.REFUNDED, label: PAYMENT_STATUS.REFUNDED },
        ]}
      />
    ),
    filter: selectFilter,
  },
  {
    Header: "Сумма исполнителя, руб.",
    id: "executor_price",
    accessor: (order) => order.executor_price ?? 0,
    Cell: ({ row }: any) => (
      <>
        {row.original.executor_price != null
          ? formatMoney(row.original.executor_price)
          : "—"}
      </>
    ),
    Filter: (filter) => <TextColumnFilter {...filter} />,
    filter: makeAmountFilter((order) => order.executor_price),
  },
  {
    Header: "Вид техники",
    id: "vehicle_category",
    accessor: (order) => order.vehicle_category?.name ?? "—",
    Cell: ({ row }: any) => (
      <span className="flex items-center whitespace-nowrap">
        {row.original.vehicle_category?.icon?.path && (
          <img
            className="mr-[10px]"
            width={20}
            height={20}
            src={row.original.vehicle_category.icon.path}
            alt="Manipulator"
          />
        )}
        {row.original.vehicle_category?.name}
      </span>
    ),
    Filter: (filter) => (
      <SelectColumnFilter {...filter} options={vehicleCategoryOptions} />
    ),
    filter: selectFilter,
  },
  {
    Header: "Менеджер компании",
    id: "executor_manager",
    accessor: (order) => fullName(order.executor_company?.owner),
    Filter: (filter) => <TextColumnFilter {...filter} />,
  },
];
